import { z } from "zod";

const BookingSchema = z.object({
    id: z.string().uuid(),
    property_id: z.string().uuid(),
    room_type_id: z.nullable(z.string().uuid()),
    room_id: z.nullable(z.string().uuid()),
    rate_plan_id: z.nullable(z.string().uuid()),
    checkin_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // Ensures YYYY-MM-DD format
    checkout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    status: z.nullable(z.string()).optional(),
    mark: z.nullable(z.string()).optional(),
    payment_type: z.nullable(z.string()).optional(),
    created_at: z.string(),
    updated_at: z.string(),
    source: z.string(),
    invoice_number: z.string(),
    invoice_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    customer_name: z.string(),
    customer_surname: z.nullable(z.string()),
    customer_email: z.nullable(z.string()),
    voucher_number: z.string().nullable(),
    ota_name: z.string(),
    ota_primary_color: z.nullable(z.string().regex(/^#([0-9A-Fa-f]{6})$/)).optional(), // Ensures valid hex color
    ota_secondary_color: z.nullable(z.string().regex(/^#([0-9A-Fa-f]{6})$/)).optional(),
    agent_color: z.nullable(z.string().regex(/^#([0-9A-Fa-f]{6})$/)).optional(),
    total_payments: z.nullable(z.string()).optional(),
    down_payment: z.nullable(z.string()).optional(),
    due_payment: z.nullable(z.string()).optional(),
    total_rate: z.nullable(z.string()).optional(),
    // Dipakai hover tooltip kalender reservasi hanya untuk menandai ADANYA
    // catatan (isinya tidak ditampilkan di tooltip).
    //
    // Sengaja `.nullish()` + optional: backend baru menambahkan kedua field ini
    // ke select `/v3/booking-calendar`, jadi FE harus tetap lolos validasi kalau
    // dijalankan terhadap backend versi lama (indikator komentar cuma tidak
    // muncul, tanpa error). Ini membuat urutan deploy FE/BE tidak mengikat.
    public_note: z.string().nullish(),
    private_note: z.string().nullish(),
    // Menandai booking hasil bulk-import di channel manager (bukan reservasi
    // normal) — dipakai untuk ikon "imported" di event kalender & hover tooltip.
    // `.nullish()` dengan alasan yang sama seperti dua field di atas: FE harus
    // tetap lolos validasi terhadap backend versi lama.
    is_imported: z.boolean().nullish(),
    // Penanda hasil split reservasi (lihat booking_rooms.split_date /
    // parent_booking_room_id di backend). `.optional()` supaya FE tetap lolos
    // validasi terhadap backend versi lama yang belum mengekspos field ini.
    split_date: z.string().nullable().optional(),
    parent_booking_room_id: z.string().nullable().optional(),
    split_role: z.enum(['segment_1', 'segment_2']).nullable().optional(),
    // Booking punya minimal 1 extra/service AKTIF (bukan hasil POS yang sudah
    // dibatalkan, prefix "[BATAL]" pada item_name) — dipakai untuk ikon "Extra"
    // di event kalender & hover tooltip, pola sama seperti is_imported.
    // `.nullish()` dengan alasan yang sama: FE tetap lolos validasi terhadap
    // backend versi lama yang belum mengirim field ini.
    has_extra: z.boolean().nullish(),
    // Jumlah item extra AKTIF (predikat sama persis dengan has_extra) —
    // dipakai hover tooltip untuk menampilkan baris "Extra" dengan nilai
    // jumlah item, alih-alih cuma ikon marker. `.nullish()` dengan alasan
    // yang sama seperti has_extra: FE tetap lolos validasi terhadap backend
    // versi lama yang belum mengirim field ini.
    extra_count: z.number().nullish(),
});

const DataCalendarResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.array(BookingSchema),
});

export { DataCalendarResponseSchema, BookingSchema };

export type DataCalendarResponse = z.infer<typeof DataCalendarResponseSchema>;
export type BookingType = z.infer<typeof BookingSchema>;